// ASK-142 R6 — THE LENS IS CONSTRAINED ON BOTH AXES, NOT ONE (Unreal's "Aspect Ratio Axis
// Constraint"; the Hor+ / Vert- hybrid every shipped game uses for phones and ultrawides).
//
// three.js's `fov` is VERTICAL. The stage held it at 42° for every aspect, so the HORIZONTAL view —
// what the eye reads as "the lens" — was a function of the screen's shape: measured on an iPhone 15,
// 25.8° in portrait (a telephoto tunnel) and 65.5° in landscape, a 2.5× change on every rotation. That
// is the distortion Shaun saw.
//
// The rule now:
//   · wide screens (desktop, landscape phone): the DESIGN VERTICAL 42° holds — Hor+, the screen's
//     extra width shows more of the shop. Desktop and landscape are pixel-unchanged.
//   · narrow screens (portrait phone, split view): the horizontal view never falls below the
//     DESIGN HORIZONTAL — the vertical opens up to keep it (Vert-) …
//   · … until the vertical would pass 85°, where perspective stretch at the screen's top and bottom
//     edges costs more than the lost width. There the vertical is clamped and the horizontal yields.
//
// The design horizontal is NOT a free number: it is what the design vertical already gives at 3:2,
// the narrowest landscape a desktop browser window or a phone on its side presents (≈ 59.9°). Every
// screen 3:2 or wider therefore keeps today's exact lens — a literal 65° (ASK-142 §6 R6's figure,
// taken from the iPhone's 1.68 landscape) would have opened a 16:10 MacBook window by 1.4°.
//
// iPhone 15 portrait (aspect 0.596): v 85°, h 57.3° (was 25.8°); landscape (1.677): v 42°, h 65.5°.

export const DESIGN_VFOV_DEG = 42;
export const HOR_PLUS_MIN_ASPECT = 3 / 2;
export const MAX_VFOV_DEG = 85;

const RAD = Math.PI / 180;

/** Horizontal FOV (degrees) a vertical FOV gives at this aspect (width / height). */
export function hfovOf(vfovDeg, aspect) {
	return 2 * Math.atan(Math.tan((vfovDeg * RAD) / 2) * aspect) / RAD;
}

/** Vertical FOV (degrees) that yields this horizontal FOV at this aspect. */
export function vfovOf(hfovDeg, aspect) {
	return 2 * Math.atan(Math.tan((hfovDeg * RAD) / 2) / aspect) / RAD;
}

export const DESIGN_HFOV_DEG = hfovOf(DESIGN_VFOV_DEG, HOR_PLUS_MIN_ASPECT);

/** The camera's vertical FOV (degrees) for a viewport aspect — the one policy, every mode. */
export function fovForAspect(aspect) {
	if (!(aspect > 0) || !Number.isFinite(aspect)) return DESIGN_VFOV_DEG;
	return Math.min(MAX_VFOV_DEG, Math.max(DESIGN_VFOV_DEG, vfovOf(DESIGN_HFOV_DEG, aspect)));
}
